'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import IosShareIcon from '@mui/icons-material/IosShare';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { EmptyState } from '@/components';
import { useT } from '@/hooks';
import { MarketClosedPosition, MarketPosition } from '@/types';
import { formatCurrency, formatSportPrice } from '@/utils';

interface ProfilePositionsTableContentProps {
  positions: (MarketPosition | MarketClosedPosition)[];
  tab: string;
  isLoading?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const ProfilePositionsTableContent = ({
  positions,
  tab,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProfilePositionsTableContentProps) => {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useT();

  if (isLoading) {
    const border = alpha(theme.palette.text.primary, 0.05);
    return (
      <Box sx={{ position: 'relative', mt: 1 }}>
        {/* ── Mobile Card List (hidden on md+) ── */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            border: '1px solid',
            borderColor: border,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                gap: 2,
                borderBottom: i < 4 ? '1px solid' : 'none',
                borderColor: border,
              }}
            >
              <Box
                sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, minWidth: 0 }}
              >
                <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Skeleton variant="text" width={140} height={20} />
                  <Skeleton variant="text" width={90} height={16} />
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
              >
                <Skeleton variant="text" width={60} height={20} />
                <Skeleton variant="text" width={50} height={16} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Desktop Table (hidden on xs) ── */}
        <TableContainer
          sx={{
            display: { xs: 'none', md: 'block' },
            border: '1px solid',
            borderColor: border,
            borderRadius: 2,
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none' }}>
                  <Skeleton variant="text" width={100} />
                </TableCell>
                <TableCell sx={{ border: 'none' }}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
                <TableCell sx={{ border: 'none' }}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
                <TableCell sx={{ border: 'none' }}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
                <TableCell sx={{ border: 'none' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ border: 'none' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Skeleton
                        variant="rectangular"
                        width={44}
                        height={44}
                        sx={{ borderRadius: 1.5 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="40%" />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (positions.length === 0) {
    return (
      <EmptyState
        title={t('leaderboardPortfolio.noPositions')}
        description={t('leaderboardPortfolio.noPositionsDesc')}
      />
    );
  }

  const renderMobileCard = (pos: MarketPosition | MarketClosedPosition, index: number) => {
    if (!pos) return null;

    const isClosedTab = tab === 'Closed';
    const isProfit = isClosedTab
      ? (pos.realizedPnl || 0) >= 0
      : ((pos as MarketPosition).percentPnl || 0) >= 0;

    const displayValue = isClosedTab
      ? (pos as MarketClosedPosition).totalBought || 0
      : (() => {
          const active = pos as MarketPosition;
          return active.currentValue !== undefined && active.currentValue !== null
            ? active.currentValue
            : (active.size || 0) * (active.curPrice || 0);
        })();

    let pnlPercent: number | null | undefined = null;
    let cashPnl = 0;
    if (isClosedTab) {
      const closed = pos as MarketClosedPosition;
      cashPnl = closed.realizedPnl || 0;
      const totalBought = closed.totalBought || 0;
      const costBasis = totalBought - cashPnl;
      pnlPercent = costBasis > 0 ? (cashPnl / costBasis) * 100 : 0;
    } else {
      const active = pos as MarketPosition;
      cashPnl = active.cashPnl || 0;
      pnlPercent = active.percentPnl;
    }

    const pnlDisplay =
      pnlPercent !== null && pnlPercent !== undefined
        ? `${formatCurrency(cashPnl)} (${pnlPercent >= 0 ? '+' : ''}${pnlPercent.toFixed(2)}%)`
        : null;

    return (
      <Box
        key={`mobile-${pos.conditionId || index}-${index}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
          gap: 2,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.06),
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
        }}
      >
        {/* Left: icon + title + chip */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: '1px solid',
              borderColor: alpha(theme.palette.text.primary, 0.05),
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <Image
              src={pos.icon}
              alt={pos.title || 'Market'}
              fill
              sizes="44px"
              style={{ objectFit: 'cover' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
            >
              {pos.title || '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: alpha(isProfit ? '#4ec38a' : '#e0664f', 0.1),
                  color: isProfit ? '#4ec38a' : '#e0664f',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {pos.outcome || '-'}
                {!isClosedTab && (
                  <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                    &nbsp;{formatSportPrice(pos.avgPrice || 0)}
                  </Typography>
                )}
              </Box>
              {!isClosedTab && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {((pos as MarketPosition).size || 0).toLocaleString()}{' '}
                  {t('leaderboardPortfolio.shares')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: value + pnl */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.25,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}
          >
            {formatCurrency(displayValue || 0)}
          </Typography>
          {pnlDisplay && (
            <Typography
              variant="caption"
              sx={{
                color: isProfit ? '#4ec38a' : '#e0664f',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {pnlDisplay}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderDesktopRow = (pos: MarketPosition | MarketClosedPosition, index: number) => {
    if (!pos) return null;

    const isClosedTab = tab === 'Closed';
    const isProfit = isClosedTab
      ? (pos.realizedPnl || 0) >= 0
      : ((pos as MarketPosition).percentPnl || 0) >= 0;

    const displayValue = isClosedTab
      ? (pos as MarketClosedPosition).totalBought || 0
      : (() => {
          const active = pos as MarketPosition;
          return active.currentValue !== undefined && active.currentValue !== null
            ? active.currentValue
            : (active.size || 0) * (active.curPrice || 0);
        })();

    let pnlPercent: number | null | undefined = null;
    let cashPnl = 0;
    if (isClosedTab) {
      const closed = pos as MarketClosedPosition;
      cashPnl = closed.realizedPnl || 0;
      const totalBought = closed.totalBought || 0;
      const costBasis = totalBought - cashPnl;
      pnlPercent = costBasis > 0 ? (cashPnl / costBasis) * 100 : 0;
    } else {
      const active = pos as MarketPosition;
      cashPnl = active.cashPnl || 0;
      pnlPercent = active.percentPnl;
    }

    const pnlDisplay =
      pnlPercent !== null && pnlPercent !== undefined
        ? `${formatCurrency(cashPnl)} (${pnlPercent >= 0 ? '+' : ''}${pnlPercent.toFixed(2)}%)`
        : null;

    return (
      <TableRow
        key={`desktop-${pos.conditionId || index}-${index}`}
        sx={{
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.05),
        }}
      >
        <TableCell sx={{ py: 2, border: 'none' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.text.primary, 0.03),
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.05),
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Image
                src={pos.icon}
                alt={pos.title || 'Market'}
                fill
                sizes="44px"
                style={{ objectFit: 'cover' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {pos.title || '-'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(isProfit ? '#4ec38a' : '#e0664f', 0.1),
                    color: isProfit ? '#4ec38a' : '#e0664f',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {pos.outcome || '-'}
                </Box>
                {!isClosedTab && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {((pos as MarketPosition).size || 0).toLocaleString()}{' '}
                    {t('leaderboardPortfolio.shares')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ border: 'none' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatSportPrice(pos.avgPrice || 0)}
          </Typography>
        </TableCell>
        <TableCell sx={{ border: 'none' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatSportPrice(pos.curPrice || 0)}
          </Typography>
        </TableCell>
        <TableCell sx={{ border: 'none' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatCurrency(displayValue || 0)}
            </Typography>
            {pnlDisplay && (
              <Typography
                variant="caption"
                sx={{
                  color: isProfit ? '#4ec38a' : '#e0664f',
                  fontWeight: 600,
                }}
              >
                {pnlDisplay}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ border: 'none', textAlign: 'right' }}>
          <Button
            variant="text"
            size="small"
            endIcon={<IosShareIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
            }}
            onClick={() => router.push(pos.eventSlug ? `/markets/${pos.eventSlug}` : '#')}
          >
            {t('leaderboardPortfolio.trade')}
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      {/* ── Mobile Card List (hidden on md+) ── */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.05),
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {positions.map(renderMobileCard)}

        {hasNextPage && fetchNextPage && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              variant="outlined"
              size="small"
              sx={{
                color: 'text.secondary',
                borderColor: alpha(theme.palette.text.primary, 0.1),
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                borderRadius: 10,
                '&:hover': {
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  bgcolor: alpha(theme.palette.text.primary, 0.02),
                  color: 'text.primary',
                },
              }}
            >
              {isFetchingNextPage ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CircularProgress size={14} color="inherit" thickness={6} />
                  {t('leaderboardPortfolio.loading')}
                </Box>
              ) : (
                t('leaderboardPortfolio.loadMore')
              )}
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Desktop Table (hidden on xs) ── */}
      <TableContainer
        sx={{
          display: { xs: 'none', md: 'block' },
          maxHeight: 500,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.05),
          borderRadius: 2,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.text.primary, 0.05),
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                  border: 'none',
                }}
              >
                {t('leaderboardPortfolio.market')}
              </TableCell>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                  border: 'none',
                }}
              >
                {t('leaderboardPortfolio.avg')}
              </TableCell>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                  border: 'none',
                }}
              >
                {t('leaderboardPortfolio.current')}
              </TableCell>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                  border: 'none',
                }}
              >
                {tab === 'Closed'
                  ? t('leaderboardPortfolio.amountWon')
                  : t('leaderboardPortfolio.value')}
              </TableCell>
              <TableCell sx={{ border: 'none' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map(renderDesktopRow)}

            {hasNextPage && fetchNextPage && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="outlined"
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      borderColor: alpha(theme.palette.text.primary, 0.1),
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      px: 3,
                      borderRadius: 10,
                      '&:hover': {
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        bgcolor: alpha(theme.palette.text.primary, 0.02),
                        color: 'text.primary',
                      },
                    }}
                  >
                    {isFetchingNextPage ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={14} color="inherit" thickness={6} />
                        {t('leaderboardPortfolio.loading')}
                      </Box>
                    ) : (
                      t('leaderboardPortfolio.loadMore')
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
